/*
 * @Author       : Zhelin Cheng
 * @Date         : 2021-01-11 11:00:23
 * @LastEditors  : 程哲林
 * @LastEditTime : 2024-04-03 15:01:48
 * @FilePath     : /marketing-introduce-ssr/utils/poster.ts
 * @Description  : 未添加文件描述
 */

import { POSTER_ZOOM } from '@/common/const';

export interface StyleType {
  x: number;
  y: number;
  width: number;
  height: number;
  borderRadius: number;

  color: string;
  fontSize: number;
  textAlign: 'start' | 'end' | 'left' | 'right' | 'center';
  textBaseline: 'top' | 'hanging' | 'middle' | 'alphabetic' | 'ideographic' | 'bottom';
  fontFamily: string;
  fontWeight: 'normal' | 'bold' | 100 | 200 | 300 | 400 | 500 | 600;
}

export interface StructureType {
  type: 'img' | 'font' | 'shape';
  src?: string;
  content?: string;
  unit?: string;
  style: Partial<StyleType>;
  unitStyle?: Partial<StyleType>;
}

// 用来描述单个文字的具体样式，大小、字体等。
export interface IFonts {
  lineHeight: number;
  fontStyle: string;
  fontFamily: string;
  fontWeight: number;
  fontSize: number;
}

// 用来描述文字排布
export interface IText {
  textAlign: 'left' | 'right' | 'center';
  lineClamp?: number;
  textDecoration: 'line-through';
}

export class CanvasRenderer {
  private canvas: HTMLCanvasElement;

  private context: CanvasRenderingContext2D | null;

  private isHttps: boolean;

  private config = {
    width: 0,
    height: 0,
    dpr: 1,
    backgroundColor: '#fff'
  };

  constructor(config: { width: number; height: number; dpr?: number; backgroundColor?: string }) {
    this.isHttps = window.location.protocol.includes('https');
    // console.log(`https:`, this.isHttps);
    const drawing = document.createElement('canvas');

    let dpr = config.dpr || window.devicePixelRatio;
    dpr = dpr > 2 ? 2 : dpr;

    this.config = {
      dpr,
      width: config.width * dpr,
      height: config.height * dpr,
      backgroundColor: config.backgroundColor || '#fff'
    };

    if (drawing && drawing.getContext) {
      const context = drawing.getContext('2d') as CanvasRenderingContext2D;
      drawing.width = this.config.width;
      drawing.height = this.config.height;

      // drawing
      this.context = context;
    } else {
      this.context = null;
    }

    // console.log(this.config);
    this.canvas = drawing;
  }

  drawRoundRectPath(cxt: CanvasRenderingContext2D, width: number, height: number, radius: number) {
    cxt.beginPath();
    // 从右下角顺时针绘制，弧度从0到1/2PI
    cxt.arc(width - radius, height - radius, radius, 0, Math.PI / 2);

    // 矩形下边线
    cxt.lineTo(radius, height);

    // 左下角圆弧，弧度从1/2PI到PI
    cxt.arc(radius, height - radius, radius, Math.PI / 2, Math.PI);

    // 矩形左边线
    cxt.lineTo(0, radius);

    // 左上角圆弧，弧度从PI到3/2PI
    cxt.arc(radius, radius, radius, Math.PI, (Math.PI * 3) / 2);

    // 上边线
    cxt.lineTo(width - radius, 0);

    // 右上角圆弧
    cxt.arc(width - radius, radius, radius, (Math.PI * 3) / 2, Math.PI * 2);

    // 右边线
    cxt.lineTo(width, height - radius);
    cxt.closePath();
  }

  strokeRoundRect(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    lineWidth: number,
    strokeColor: string
  ) {
    const cxt = context;
    // 圆的直径必然要小于矩形的宽高
    if (2 * radius > width || 2 * radius > height) {
      return;
    }

    cxt.save();
    cxt.translate(x, y);
    // 绘制圆角矩形的各个边
    this.drawRoundRectPath(cxt, width, height, radius);
    cxt.lineWidth = lineWidth || 2; // 若是给定了值就用给定的值否则给予默认值2
    cxt.strokeStyle = strokeColor || '#000';
    cxt.stroke();
    cxt.restore();
  }

  fillRoundRect(
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
    fillColor: string
  ) {
    const cxt = context;
    // 圆的直径必然要小于矩形的宽高
    if (2 * radius > width || 2 * radius > height) {
      return;
    }

    cxt.save();
    cxt.translate(x, y);
    // 绘制圆角矩形的各个边
    this.drawRoundRectPath(cxt, width, height, radius);
    cxt.fillStyle = fillColor || '#000'; // 若是给定了值就用给定的值否则给予默认值
    cxt.fill();
    cxt.restore();
  }

  /**
   * 清除画布
   * @param x
   * @param y
   * @param width
   * @param height
   */
  clear(x = 0, y = 0, width = this.config.width, height = this.config.height) {
    // eslint-disable-next-line no-unused-expressions
    if (this.context) {
      this.context.clearRect(x, y, width, height);
    }
  }

  async render(structure: StructureType[]): Promise<HTMLCanvasElement> {
    this.clear();
    const ctx = this.context;
    if (ctx) {
      const conf = this.config;
      const { dpr } = conf;

      const images = structure.map(({ src = '' }) => src).filter((src) => Boolean(src));
      const imagesLoad = await Promise.all(
        images.map((src) => {
          return this.loadImage(src);
        })
      );
      const imagesMemo: Record<string, HTMLImageElement> = {};

      for (let index = 0; index < images.length; index += 1) {
        const rImg = imagesLoad[index];
        if (rImg) {
          imagesMemo[images[index]] = rImg;
        }
      }
      /* images.forEach((src, index) => {
        imagesMemo[src] = imagesLoad[index];
      }); */

      ctx.fillStyle = this.config.backgroundColor;
      ctx.fillRect(0, 0, conf.width, conf.height);
      for (const { type, src = '', content = '', style = {}, unit, unitStyle } of structure) {
        const ns: any = {
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          ...style
        };

        if (dpr !== 1) {
          const styleKeys = Object.keys(ns);
          const exclude = ['fontWeight'];
          for (const key of styleKeys) {
            const val = ns[key];
            if (typeof val === 'number' && !exclude.includes(key)) {
              ns[key] = val * dpr;
            }
          }
        }

        ctx.save();
        switch (type) {
          case 'img':
            // eslint-disable-next-line no-case-declarations
            const img = imagesMemo[src];

            if (!img) {
              break;
            }

            if (ns.borderRadius) {
              const maxRadius = Math.floor(Math.min(ns.width, ns.height) / 2);
              const radius = ns.borderRadius;

              if (radius > maxRadius) {
                this.circleImgComplete(img, ns.x, ns.y, maxRadius);
              } else {
                this.circleImgAngle(img, ns.x, ns.y, ns.width, ns.height, radius);
              }
            } else {
              ctx.drawImage(img, 0, 0, img.width, img.height, ns.x, ns.y, ns.width, ns.height);
            }

            break;
          case 'font':
            // ctx.fillText()
            ctx.font = `${ns.fontWeight || 'normal'} ${ns.fontSize || 14}px ${
              ns.fontFamily || 'Arial'
            }`;
            ctx.textAlign = ns.textAlign || 'start';
            ctx.textBaseline = ns.textBaseline || 'middle';
            if (ns.color) {
              ctx.fillStyle = ns.color;
            }
            ctx.fillText(content || '', ns.x, ns.y);

            if (unit && unitStyle) {
              const ctWidth = ctx.measureText(content).width;
              ctx.restore();
              // 设置另一种字体大小和样式
              ctx.font = `${unitStyle.fontSize}px Arial`;
              ctx.textBaseline = 'middle';
              ctx.fillStyle = unitStyle.color || '#000';
              const baseZoom = POSTER_ZOOM;
              ctx.fillText(
                unit,
                ns.x + ctWidth,
                ns.y + ((ns.fontSize - (unitStyle.fontSize || 0)) / 2) * baseZoom
              );
            }

            break;
          case 'shape':
            this.fillRoundRect(
              ctx,
              ns.x,
              ns.y,
              ns.width,
              ns.height,
              ns.borderRadius,
              ns.backgroundColor || '#000'
            );
            break;
          default:
        }

        ctx.restore();
      }
    }

    return this.canvas;
  }

  /**
   * 加载图片
   * @param src 图片地址
   */
  // eslint-disable-next-line class-methods-use-this
  loadImage(src: string): Promise<HTMLImageElement | undefined> {
    const { isHttps } = this;

    return new Promise((resolve) => {
      const img = new Image();
      if (!/^data:/.test(src)) {
        img.setAttribute('crossOrigin', 'anonymous');
      }
      img.src = isHttps && /^http:/i.test(src) ? src.replace(/^http:/i, 'https:') : src;
      img.onload = () => {
        resolve(img);
      };
      img.onerror = (e) => {
        console.error(e);
        resolve(undefined);
      };
    });
  }

  /*
   *  绘制圆形
   *  img 图片地址
   *   x  x轴坐标
   *   y  y轴坐标
   *   r  圆形半径
   */

  /**
   * 绘制圆
   * @param img 图片
   * @param x x轴坐标
   * @param y y轴坐标
   * @param r 圆形半径
   */
  circleImgComplete(img: HTMLImageElement, x: number, y: number, r: number) {
    const ctx = this.context;

    if (ctx) {
      const d = r * 2;
      const cx = x + r;
      const cy = y + r;
      ctx.arc(cx, cy, r, 0, 2 * Math.PI);
      ctx.strokeStyle = '#FFFFFF'; // 设置绘制圆形边框的颜色
      ctx.stroke(); // 绘制出圆形，默认为黑色，可通过 ctx.strokeStyle = '#FFFFFF'， 设置想要的颜色
      ctx.clip();
      ctx.drawImage(img, x, y, d, d);
    }
  }

  /**
   * 绘制圆角
   * @param img 图片
   * @param x x轴坐标
   * @param y y轴坐标
   * @param w 宽度
   * @param h 高度
   * @param r 弧度大小
   */
  circleImgAngle(img: HTMLImageElement, x: number, y: number, w: number, h: number, r: number) {
    const ctx = this.context;

    if (ctx) {
      let nr = r;
      if (w < 2 * r) nr = w / 2;
      if (h < 2 * r) nr = h / 2;
      ctx.beginPath();
      ctx.moveTo(x + nr, y);
      ctx.arcTo(x + w, y, x + w, y + h, nr);
      ctx.arcTo(x + w, y + h, x, y + h, nr);
      ctx.arcTo(x, y + h, x, y, nr);
      ctx.arcTo(x, y, x + w, y, nr);
      ctx.closePath();
      ctx.strokeStyle = '#FFFFFF'; // 设置绘制圆形边框的颜色
      ctx.stroke();
      ctx.clip();
      ctx.drawImage(img, x, y, w, h);
    }
  }

  /**
   * 获取base64
   * @param type 格式
   * @param quality 质量
   */
  toDataURL(type?: string, quality?: any) {
    return this.canvas ? this.canvas.toDataURL(type, quality) : '';
  }
}
